import {
  AlertTriangle,
  Calendar,
  Check,
  CircleDot,
  Contact,
  Eye,
  EyeOff,
  Fuel,
  Hash,
  Lightbulb,
  MapPin,
  Pencil,
  Plane,
  SeparatorHorizontal,
  Stethoscope,
  ToggleLeft,
  TriangleAlert,
  Type,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useIntl } from '@/core/i18n/use-intl';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/shared/components/ui';
import { cn } from '@/shared/utils';
import type { CustomerAccess, FieldType, FieldWidth, MetadataField } from './types';

interface MetadataFieldDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  field?: MetadataField | null;
  onSave: (field: Partial<MetadataField>) => void;
  allowedCustomerAccess?: CustomerAccess[];
  /** Name of existing group indicator field (if any) in the current group */
  existingGroupIndicatorName?: string | null;
}

const fieldTypeOptions: {
  value: FieldType;
  icon: React.ComponentType<{ className?: string }>;
  descriptionKey: string;
}[] = [
  {
    value: 'string',
    icon: Type,
    descriptionKey: 'fieldTypeDescriptions.string',
  },
  {
    value: 'number',
    icon: Hash,
    descriptionKey: 'fieldTypeDescriptions.number',
  },
  {
    value: 'boolean',
    icon: ToggleLeft,
    descriptionKey: 'fieldTypeDescriptions.boolean',
  },
  {
    value: 'date',
    icon: Calendar,
    descriptionKey: 'fieldTypeDescriptions.date',
  },
  {
    value: 'contact',
    icon: Contact,
    descriptionKey: 'fieldTypeDescriptions.contact',
  },
  {
    value: 'positions',
    icon: MapPin,
    descriptionKey: 'fieldTypeDescriptions.positions',
  },
  {
    value: 'status',
    icon: CircleDot,
    descriptionKey: 'fieldTypeDescriptions.status',
  },
  {
    value: 'section',
    icon: SeparatorHorizontal,
    descriptionKey: 'fieldTypeDescriptions.section',
  },
];

// Status field icon options - 'none' used instead of '' because Radix Select doesn't allow empty strings
const statusIconOptions = [
  { value: 'none', icon: null, labelKey: 'metadata.status.noIcon' },
  { value: 'fuel', icon: Fuel, labelKey: 'metadata.status.icons.fuel' },
  {
    value: 'helideck',
    icon: Plane,
    labelKey: 'metadata.status.icons.helideck',
  },
  {
    value: 'warning',
    icon: TriangleAlert,
    labelKey: 'metadata.status.icons.warning',
  },
  { value: 'check', icon: Check, labelKey: 'metadata.status.icons.check' },
  {
    value: 'lightning',
    icon: Lightbulb,
    labelKey: 'metadata.status.icons.lightning',
  },
  {
    value: 'medical',
    icon: Stethoscope,
    labelKey: 'metadata.status.icons.medical',
  },
] as const;

type StatusIcon = (typeof statusIconOptions)[number]['value'];

interface StatusOption {
  color: 'red' | 'yellow' | 'green';
  labelDe: string;
  labelEn: string;
}

export function MetadataFieldDialog({
  open,
  onOpenChange,
  field,
  onSave,
  allowedCustomerAccess = ['none', 'read', 'read-and-write'],
  existingGroupIndicatorName = null,
}: MetadataFieldDialogProps) {
  const { t } = useIntl();
  const isEditing = !!field?.key && !field.key.startsWith('temp-');

  // Check if isGroupIndicator checkbox should be disabled
  // It's disabled if another field in the same group already has it enabled
  // But NOT disabled if the current field is that indicator (so they can toggle it off)
  const isCurrentFieldTheIndicator = field?.isGroupIndicator === true;
  const anotherFieldHasIndicator = existingGroupIndicatorName && !isCurrentFieldTheIndicator;
  const isGroupIndicatorDisabled = !!anotherFieldHasIndicator;

  const [type, setType] = useState<FieldType>('string');
  const [labelDe, setLabelDe] = useState('');
  const [labelEn, setLabelEn] = useState('');
  const [customerAccess, setCustomerAccess] = useState<CustomerAccess>('read');
  const [width, setWidth] = useState<FieldWidth>('50');
  const [required, setRequired] = useState(false);
  const [statusLabel, setStatusLabel] = useState('');
  const [statusLabelEn, setStatusLabelEn] = useState('');

  // Status field specific state
  const [enableYellow, setEnableYellow] = useState(false);
  const [greenLabelDe, setGreenLabelDe] = useState('');
  const [greenLabelEn, setGreenLabelEn] = useState('');
  const [yellowLabelDe, setYellowLabelDe] = useState('');
  const [yellowLabelEn, setYellowLabelEn] = useState('');
  const [redLabelDe, setRedLabelDe] = useState('');
  const [redLabelEn, setRedLabelEn] = useState('');
  const [isGroupIndicator, setIsGroupIndicator] = useState(false);
  const [statusIcon, setStatusIcon] = useState<StatusIcon>('none');

  // Reset form when field changes
  useEffect(() => {
    if (field) {
      setType(field.type);
      setLabelDe(field.labelDe);
      setLabelEn(field.labelEn);
      setCustomerAccess(field.customerAccess);
      setWidth(field.width);
      setRequired(field.required);
      setStatusLabel(field.statusLabel ?? '');
      setStatusLabelEn(field.statusLabelEn ?? '');

      // Status field specific
      const statusOpts = field.statusOptions ?? [];
      const greenOpt = statusOpts.find((o) => o.color === 'green');
      const yellowOpt = statusOpts.find((o) => o.color === 'yellow');
      const redOpt = statusOpts.find((o) => o.color === 'red');
      setGreenLabelDe(greenOpt?.labelDe ?? '');
      setGreenLabelEn(greenOpt?.labelEn ?? '');
      setYellowLabelDe(yellowOpt?.labelDe ?? '');
      setYellowLabelEn(yellowOpt?.labelEn ?? '');
      setRedLabelDe(redOpt?.labelDe ?? '');
      setRedLabelEn(redOpt?.labelEn ?? '');
      setEnableYellow(!!yellowOpt);
      setIsGroupIndicator(field.isGroupIndicator ?? false);
      setStatusIcon((field.icon as StatusIcon) ?? 'none');
    } else {
      setType('string');
      setLabelDe('');
      setLabelEn('');
      setCustomerAccess('read');
      setWidth('50');
      setRequired(false);
      setStatusLabel('');
      setStatusLabelEn('');

      // Reset status fields
      setEnableYellow(false);
      setGreenLabelDe('');
      setGreenLabelEn('');
      setYellowLabelDe('');
      setYellowLabelEn('');
      setRedLabelDe('');
      setRedLabelEn('');
      setIsGroupIndicator(false);
      setStatusIcon('none');
    }
  }, [field, open]);

  const handleSave = () => {
    if (!labelDe.trim()) {
      return;
    }

    const isFullWidthType =
      type === 'section' || type === 'contact' || type === 'positions' || type === 'status';

    // Build status options if status field
    let statusOptions: StatusOption[] | undefined;
    if (type === 'status') {
      statusOptions = [
        {
          color: 'green',
          labelDe: greenLabelDe.trim() || 'Aktiv',
          labelEn: greenLabelEn.trim() || 'Active',
        },
        ...(enableYellow
          ? [
              {
                color: 'yellow' as const,
                labelDe: yellowLabelDe.trim() || 'Eingeschränkt',
                labelEn: yellowLabelEn.trim() || 'Limited',
              },
            ]
          : []),
        {
          color: 'red',
          labelDe: redLabelDe.trim() || 'Inaktiv',
          labelEn: redLabelEn.trim() || 'Inactive',
        },
      ];
    }

    onSave({
      type,
      labelDe: labelDe.trim(),
      labelEn: labelEn.trim(),
      customerAccess,
      width: isFullWidthType ? '100' : width,
      required: type === 'section' || type === 'positions' || type === 'status' ? false : required,
      ...(type === 'positions' && statusLabel.trim() && { statusLabel: statusLabel.trim() }),
      ...(type === 'positions' && statusLabelEn.trim() && { statusLabelEn: statusLabelEn.trim() }),
      ...(type === 'status' && {
        statusOptions,
        isGroupIndicator,
        ...(statusIcon && statusIcon !== 'none' && { icon: statusIcon }),
      }),
    });

    onOpenChange(false);
  };

  const customerAccessOptions = [
    { value: 'none', label: t('metadata.customerAccess.none'), icon: EyeOff },
    { value: 'read', label: t('metadata.customerAccess.read'), icon: Eye },
    {
      value: 'read-and-write',
      label: t('metadata.customerAccess.readAndWrite'),
      icon: Pencil,
    },
  ].filter((opt) => allowedCustomerAccess.includes(opt.value as CustomerAccess));

  const widthOptions: { value: FieldWidth; label: string }[] = [
    { value: '33', label: '33%' },
    { value: '50', label: '50%' },
    { value: '100', label: '100%' },
  ];

  const isSection = type === 'section';
  const isContact = type === 'contact';
  const isPositions = type === 'positions';
  const isStatus = type === 'status';
  const isFullWidthType = isSection || isContact || isPositions || isStatus;

  // Get the icon for the current type (for readonly display)
  const CurrentTypeIcon = fieldTypeOptions.find((opt) => opt.value === type)?.icon || Type;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isEditing ? t('metadata.builder.editField') : t('metadata.builder.addField')}
          </DialogTitle>
          <DialogDescription>
            {isSection
              ? t('metadata.builder.sectionDescription')
              : isContact
                ? t('metadata.builder.contactDescription')
                : isPositions
                  ? t('metadata.builder.positionsDescription')
                  : isStatus
                    ? t('metadata.builder.statusDescription')
                    : t('metadata.builder.fieldDescription')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4 overflow-y-auto flex-1">
          {/* Field Type - Readonly when editing, Card Select when creating */}
          {isEditing ? (
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('common.type')}</Label>
              <div className="flex items-center gap-3 p-3 rounded-lg border bg-muted/30">
                <CurrentTypeIcon className="size-5 text-muted-foreground" />
                <div>
                  <span className="font-medium">{t(`metadata.fieldTypes.${type}`)}</span>
                  <p className="text-xs text-muted-foreground">
                    {t('metadata.builder.typeCannotBeChanged')}
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <Label className="text-sm font-medium">{t('common.type')}</Label>
              <div className="grid grid-cols-3 gap-2">
                {fieldTypeOptions.map((option) => {
                  const Icon = option.icon;
                  const isSelected = type === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setType(option.value)}
                      className={cn(
                        'flex flex-col items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all',
                        isSelected
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-border bg-background hover:border-primary/50 hover:bg-muted/50 text-muted-foreground hover:text-foreground'
                      )}
                    >
                      <Icon className="size-5" />
                      <span className="text-xs font-medium">
                        {t(`metadata.fieldTypes.${option.value}`)}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Labels */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="labelDe">
                {t('common.label')} (DE) <span className="text-destructive">*</span>
              </Label>
              <Input
                id="labelDe"
                value={labelDe}
                onChange={(e) => setLabelDe(e.target.value)}
                placeholder={isSection ? 'Abschnittstitel' : 'Bezeichnung'}
                autoFocus
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="labelEn">{t('common.label')} (EN)</Label>
              <Input
                id="labelEn"
                value={labelEn}
                onChange={(e) => setLabelEn(e.target.value)}
                placeholder={isSection ? 'Section title' : 'Label'}
              />
            </div>
          </div>

          {/* Access Level & Width (not for sections) */}
          {!isSection && (
            <div className={cn('grid gap-4', isFullWidthType ? 'grid-cols-1' : 'grid-cols-2')}>
              <div className="space-y-2">
                <Label>{t('metadata.customerAccess.label')}</Label>
                <Select
                  value={customerAccess}
                  onValueChange={(v) => setCustomerAccess(v as CustomerAccess)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {customerAccessOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="size-4" />
                          <span>{opt.label}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!isFullWidthType && (
                <div className="space-y-2">
                  <Label>{t('metadata.builder.width')}</Label>
                  <Select value={width} onValueChange={(v) => setWidth(v as FieldWidth)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {widthOptions.map((opt) => (
                        <SelectItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* Status label for positions fields */}
          {isPositions && (
            <div className="space-y-3">
              <Label className="text-sm font-medium">
                {t('metadata.positions.statusLabelTitle')}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t('metadata.positions.statusLabelDescription')}
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="statusLabel">{t('metadata.positions.statusLabel')} (DE)</Label>
                  <Input
                    id="statusLabel"
                    value={statusLabel}
                    onChange={(e) => setStatusLabel(e.target.value)}
                    placeholder={t('metadata.positions.statusLabelPlaceholder')}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="statusLabelEn">{t('metadata.positions.statusLabel')} (EN)</Label>
                  <Input
                    id="statusLabelEn"
                    value={statusLabelEn}
                    onChange={(e) => setStatusLabelEn(e.target.value)}
                    placeholder={t('metadata.positions.statusLabelPlaceholderEn')}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Status field configuration */}
          {isStatus && (
            <div className="space-y-4">
              {/* Status Options */}
              <div className="space-y-3">
                <Label className="text-sm font-medium">{t('metadata.status.statusOptions')}</Label>
                <p className="text-xs text-muted-foreground">
                  {t('metadata.status.statusOptionsDescription')}
                </p>

                {/* Green */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="greenLabelDe" className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full bg-green-500" />
                      {t('metadata.status.greenLabel')} (DE)
                    </Label>
                    <Input
                      id="greenLabelDe"
                      value={greenLabelDe}
                      onChange={(e) => setGreenLabelDe(e.target.value)}
                      placeholder={t('metadata.status.greenLabelPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="greenLabelEn">{t('metadata.status.greenLabel')} (EN)</Label>
                    <Input
                      id="greenLabelEn"
                      value={greenLabelEn}
                      onChange={(e) => setGreenLabelEn(e.target.value)}
                      placeholder="e.g. Open, Active"
                    />
                  </div>
                </div>

                {/* Yellow (optional) */}
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="enableYellow"
                      checked={enableYellow}
                      onCheckedChange={(checked) => setEnableYellow(checked === true)}
                    />
                    <Label
                      htmlFor="enableYellow"
                      className="cursor-pointer flex items-center gap-2"
                    >
                      <span className="inline-block w-3 h-3 rounded-full bg-yellow-500" />
                      {t('metadata.status.enableYellow')}
                    </Label>
                  </div>
                  {enableYellow && (
                    <div className="grid grid-cols-2 gap-4 ml-6">
                      <div className="space-y-2">
                        <Label htmlFor="yellowLabelDe">
                          {t('metadata.status.yellowLabel')} (DE)
                        </Label>
                        <Input
                          id="yellowLabelDe"
                          value={yellowLabelDe}
                          onChange={(e) => setYellowLabelDe(e.target.value)}
                          placeholder={t('metadata.status.yellowLabelPlaceholder')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="yellowLabelEn">
                          {t('metadata.status.yellowLabel')} (EN)
                        </Label>
                        <Input
                          id="yellowLabelEn"
                          value={yellowLabelEn}
                          onChange={(e) => setYellowLabelEn(e.target.value)}
                          placeholder="e.g. Hoist, Limited"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Red */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="redLabelDe" className="flex items-center gap-2">
                      <span className="inline-block w-3 h-3 rounded-full bg-red-500" />
                      {t('metadata.status.redLabel')} (DE)
                    </Label>
                    <Input
                      id="redLabelDe"
                      value={redLabelDe}
                      onChange={(e) => setRedLabelDe(e.target.value)}
                      placeholder={t('metadata.status.redLabelPlaceholder')}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="redLabelEn">{t('metadata.status.redLabel')} (EN)</Label>
                    <Input
                      id="redLabelEn"
                      value={redLabelEn}
                      onChange={(e) => setRedLabelEn(e.target.value)}
                      placeholder="e.g. Closed, Inactive"
                    />
                  </div>
                </div>
              </div>

              {/* Icon Selection */}
              <div className="space-y-2">
                <Label>{t('metadata.status.selectIcon')}</Label>
                <Select value={statusIcon} onValueChange={(v) => setStatusIcon(v as StatusIcon)}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('metadata.status.noIcon')} />
                  </SelectTrigger>
                  <SelectContent>
                    {statusIconOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          {opt.icon && <opt.icon className="size-4" />}
                          <span>{t(opt.labelKey)}</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Group Indicator */}
              <div className="space-y-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="isGroupIndicator"
                    checked={isGroupIndicator}
                    disabled={isGroupIndicatorDisabled}
                    onCheckedChange={(checked) => setIsGroupIndicator(checked === true)}
                  />
                  <Label
                    htmlFor="isGroupIndicator"
                    className={cn(
                      'cursor-pointer',
                      isGroupIndicatorDisabled && 'text-muted-foreground cursor-not-allowed'
                    )}
                  >
                    {t('metadata.status.isGroupIndicator')}
                  </Label>
                </div>
                {isGroupIndicatorDisabled ? (
                  <p className="text-xs text-amber-600 ml-6 flex items-center gap-1.5">
                    <AlertTriangle className="size-3.5" />
                    {t('metadata.status.isGroupIndicatorAlreadyExists', {
                      fieldName: existingGroupIndicatorName,
                    })}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground ml-6">
                    {t('metadata.status.isGroupIndicatorDescription')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Required checkbox (not for sections, booleans, positions, or status) */}
          {!isSection && !isPositions && !isStatus && type !== 'boolean' && (
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="required"
                  checked={required}
                  onCheckedChange={(checked) => setRequired(checked === true)}
                />
                <Label htmlFor="required" className="cursor-pointer">
                  {t('metadata.builder.requiredField')}
                </Label>
              </div>
              {required && (
                <p className="text-xs text-amber-600 flex items-center gap-1.5 ml-6">
                  <AlertTriangle className="size-3.5" />
                  {t('metadata.builder.requiredWarning')}
                </p>
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!labelDe.trim()}>
            {isEditing ? t('common.save') : t('metadata.builder.createField')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
